import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faPhone,
  faKey,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from '../../utils';
import Swal from 'sweetalert2';
import { useSelector } from'react-redux';

const Profile = () => {
  const userData = useSelector((state) => state.user_info[0]?.user);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      customerName: userData.customerName || 'John Doe',
      customerEmail: userData.customerEmail || 'john.doe@example.com',
      customerPhone: userData.customerPhone || '9876543210'
    }
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const response = await sendApiRequest(`api/customers/profile/update/${userData._id}`, data, 'PUT');
      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Profile updated successfully',
          icon: 'success',
          confirmButtonColor: '#1DCD9F'
        });
        setIsEditing(false);
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update profile',
        icon: 'error',
        confirmButtonColor: '#1DCD9F'
      });
    }
  };

  const onPasswordSubmit = async (data) => {
    try {
      const response = await sendApiRequest(`api/customers/profile/change-password/${userData._id}`, data, 'PUT');
      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Password changed successfully',
          icon: 'success',
          confirmButtonColor: '#1DCD9F'
        });
        setIsChangingPassword(false);
        resetPassword();
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to change password',
        icon: 'error',
        confirmButtonColor: '#1DCD9F'
      });
    }
  };

  return (
    <div style={{ backgroundColor: '#F1EFEC', minHeight: '30rem' }} className="p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
        <h1
          style={{ color: '#222222' }}
          className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 md:mb-8 text-center"
        >
          My Profile
        </h1>

        {/* Profile Information */}
        <div
          style={{ backgroundColor: '#FFFFFF', borderColor: '#1DCD9F' }}
          className="border rounded-lg sm:rounded-xl mb-4 sm:mb-6 md:mb-8 p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
            <h3 style={{ color: '#222222' }} className="text-lg sm:text-xl font-semibold">
              Profile Information
            </h3>
            <button
              onClick={() => setIsEditing(!isEditing)}
              style={{ backgroundColor: '#1DCD9F', color: '#FFFFFF' }}
              className="flex items-center justify-center px-3 sm:px-4 py-2 rounded-lg hover:opacity-90 transition-all text-sm sm:text-base"
            >
              <FontAwesomeIcon icon={faEdit} className="mr-2" />
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>

          {isEditing ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
              <div>
                <label style={{ color: '#222222' }} className="block text-xs sm:text-sm font-medium mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faUser}
                    style={{ color: '#1DCD9F' }}
                    className="absolute top-1/2 left-3 transform -translate-y-1/2 text-sm sm:text-base"
                  />
                  <input
                    type="text"
                    {...register('customerName', { required: 'Name is required' })}
                    style={{ borderColor: '#1DCD9F', color: '#222222' }}
                    className="pl-9 sm:pl-10 w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-[#1DCD9F] focus:outline-none text-sm sm:text-base"
                  />
                </div>
                {errors.customerName && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.customerName.message}</p>
                )}
              </div>

              <div>
                <label style={{ color: '#222222' }} className="block text-xs sm:text-sm font-medium mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    style={{ color: '#1DCD9F' }}
                    className="absolute top-1/2 left-3 transform -translate-y-1/2 text-sm sm:text-base"
                  />
                  <input
                    type="email"
                    {...register('customerEmail', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    style={{ borderColor: '#1DCD9F', color: '#222222' }}
                    className="pl-9 sm:pl-10 w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-[#1DCD9F] focus:outline-none text-sm sm:text-base"
                  />
                </div>
                {errors.customerEmail && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.customerEmail.message}</p>
                )}
              </div>

              <div>
                <label style={{ color: '#222222' }} className="block text-xs sm:text-sm font-medium mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faPhone}
                    style={{ color: '#1DCD9F' }}
                    className="absolute top-1/2 left-3 transform -translate-y-1/2 text-sm sm:text-base"
                  />
                  <input
                    type="tel"
                    {...register('customerPhone', {
                      required: 'Phone number is required',
                      pattern: { value: /^[0-9]{10}$/, message: 'Invalid phone number' }
                    })}
                    style={{ borderColor: '#1DCD9F', color: '#222222' }}
                    className="pl-9 sm:pl-10 w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-[#1DCD9F] focus:outline-none text-sm sm:text-base"
                  />
                </div>
                {errors.customerPhone && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.customerPhone.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  style={{ backgroundColor: '#1DCD9F', color: '#FFFFFF' }}
                  className="px-4 sm:px-6 py-2 rounded-lg hover:opacity-90 transition-all shadow-md text-sm sm:text-base"
                >
                  Save Changes
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faUser} style={{ color: '#1DCD9F' }} className="mr-3 sm:mr-4 text-sm sm:text-base" />
                <div>
                  <p style={{ color: '#666666' }} className="text-xs sm:text-sm">Full Name</p>
                  <p style={{ color: '#222222' }} className="text-base sm:text-lg font-medium">
                    {userData.customerName}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#1DCD9F' }} className="mr-3 sm:mr-4 text-sm sm:text-base" />
                <div>
                  <p style={{ color: '#666666' }} className="text-xs sm:text-sm">Email Address</p>
                  <p style={{ color: '#222222' }} className="text-base sm:text-lg font-medium break-all">
                    {userData.customerEmail}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faPhone} style={{ color: '#1DCD9F' }} className="mr-3 sm:mr-4 text-sm sm:text-base" />
                <div>
                  <p style={{ color: '#666666' }} className="text-xs sm:text-sm">Phone Number</p>
                  <p style={{ color: '#222222' }} className="text-base sm:text-lg font-medium">
                    {userData.customerPhone}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Change Password */}
        <div
          style={{ backgroundColor: '#FFFFFF', borderColor: '#1DCD9F' }}
          className="border rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
            <h3 style={{ color: '#222222' }} className="text-lg sm:text-xl font-semibold">
              Change Password
            </h3>
            <button
              onClick={() => setIsChangingPassword(!isChangingPassword)}
              style={{ backgroundColor: '#1DCD9F', color: '#FFFFFF' }}
              className="flex items-center justify-center px-3 sm:px-4 py-2 rounded-lg hover:opacity-90 transition-all text-sm sm:text-base"
            >
              <FontAwesomeIcon icon={faKey} className="mr-2" />
              {isChangingPassword ? 'Cancel' : 'Change Password'}
            </button>
          </div>

          {isChangingPassword && (
            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4 sm:space-y-6">
              <div>
                <label style={{ color: '#222222' }} className="block text-xs sm:text-sm font-medium mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  {...registerPassword('currentPassword', { required: 'Current password is required' })}
                  style={{ borderColor: '#1DCD9F', color: '#222222' }}
                  className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-[#1DCD9F] focus:outline-none text-sm sm:text-base"
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                )}
              </div>

              <div>
                <label style={{ color: '#222222' }} className="block text-xs sm:text-sm font-medium mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  {...registerPassword('newPassword', {
                    required: 'New password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' }
                  })}
                  style={{ borderColor: '#1DCD9F', color: '#222222' }}
                  className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-[#1DCD9F] focus:outline-none text-sm sm:text-base"
                />
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label style={{ color: '#222222' }} className="block text-xs sm:text-sm font-medium mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  {...registerPassword('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === document.querySelector('input[name="newPassword"]').value ||
                      'Passwords do not match'
                  })}
                  style={{ borderColor: '#1DCD9F', color: '#222222' }}
                  className="w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-[#1DCD9F] focus:outline-none text-sm sm:text-base"
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  style={{ backgroundColor: '#1DCD9F', color: '#FFFFFF' }}
                  className="px-4 sm:px-6 py-2 rounded-lg hover:opacity-90 transition-all shadow-md text-sm sm:text-base"
                >
                  Update Password
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;